// Единый перевод ошибок Firebase и приложения в понятные уведомления.
// Используется в store-actions (единый catch) и в глобальных обработчиках main.js.

const NOTIFIED_FLAG = '__adminNotified'

const CODE_MESSAGES = {
  // Firestore
  'permission-denied': 'Недостаточно прав для этой операции. Возможно, ваш доступ изменили: выйдите и войдите снова.',
  unauthenticated: 'Сессия истекла. Войдите в админку заново.',
  unavailable: 'Нет связи с сервером Firebase. Проверьте подключение и повторите попытку.',
  'deadline-exceeded': 'Сервер не ответил вовремя. Повторите попытку.',
  'resource-exhausted': 'Превышена квота Firebase. Повторите попытку позже.',
  'invalid-argument': 'Сервер отклонил данные: документ больше 1 МиБ или содержит недопустимые значения (NaN, undefined).',
  'not-found': 'Документ не найден. Обновите данные и повторите.',
  'already-exists': 'Запись с таким ID уже существует. Обновите список и повторите.',
  aborted: 'Данные изменились во время записи. Обновите данные и повторите.',
  'failed-precondition': 'Операция отклонена сервером. Обновите данные и повторите.',
  cancelled: 'Операция отменена.',
  internal: 'Внутренняя ошибка Firebase. Повторите попытку позже.',
  'data-loss': 'Firebase сообщил о потере данных. Обратитесь к разработчику.',
  // Storage
  'storage/unauthorized': 'Недостаточно прав для работы с файлом в Storage.',
  'storage/unauthenticated': 'Сессия истекла. Войдите в админку заново.',
  'storage/object-not-found': 'Файл не найден в Storage.',
  'storage/quota-exceeded': 'Превышена квота Storage. Повторите попытку позже.',
  'storage/retry-limit-exceeded': 'Не удалось связаться со Storage. Проверьте подключение и повторите.',
  'storage/canceled': 'Загрузка файла отменена.',
  'storage/invalid-checksum': 'Файл поврежден при передаче. Загрузите его еще раз.',
  // Auth
  'auth/network-request-failed': 'Нет связи с сервером. Проверьте подключение.',
  'auth/too-many-requests': 'Слишком много запросов. Попробуйте позже.'
}

const STALE_NOTIFICATION = {
  type: 'warning',
  title: 'Данные изменились на сервере',
  message: 'Другой администратор сохранил эту запись после открытия редактора. Обновите данные и повторите точечную правку.'
}

const DEFAULT_MESSAGE = 'Неизвестная ошибка. Повторите попытку.'

export function errorCode(error) {
  const code = error && error.code
  if (typeof code !== 'string') return ''
  return code.trim().toLowerCase().replace(/^firestore\//, '')
}

function rawMessage(error) {
  if (typeof error === 'string') return error.trim()
  const message = error && error.message
  return typeof message === 'string' ? message.trim() : ''
}

/**
 * Превращает ошибку в payload для action `notify`.
 * Известный код Firebase -> готовый текст; свои ошибки репозитория (без кода,
 * уже на русском) показываются как есть; stale-data остается предупреждением.
 */
export function describeError(error, title = 'Ошибка') {
  if (error && error.code === 'stale-data') return { ...STALE_NOTIFICATION }

  const code = errorCode(error)
  const known = CODE_MESSAGES[code]
  if (known) return { type: 'error', title, message: `${known} (код: ${code})` }

  const message = rawMessage(error) || DEFAULT_MESSAGE
  return { type: 'error', title, message: code ? `${message} (код: ${code})` : message }
}

/**
 * Помечает ошибку как уже показанную пользователю. Глобальные обработчики
 * в main.js такую ошибку второй раз не показывают.
 */
export function markErrorNotified(error) {
  if (!error || typeof error !== 'object') return error
  try {
    Object.defineProperty(error, NOTIFIED_FLAG, { value: true, configurable: true, enumerable: false })
  } catch (definitionError) {
    // Замороженный объект: флаг не ставится, уведомление может повториться, но не потеряется.
  }
  return error
}

export function isErrorNotified(error) {
  return Boolean(error && typeof error === 'object' && error[NOTIFIED_FLAG])
}

/**
 * Единый catch для store-actions: показывает уведомление с понятным текстом
 * и помечает ошибку. Саму ошибку action обязан пробросить дальше, чтобы
 * редактор не считал операцию успешной.
 */
export async function notifyActionError(dispatch, error, title) {
  if (!error || isErrorNotified(error)) return
  markErrorNotified(error)
  await dispatch('notify', describeError(error, title))
}
